import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Keeps the URL hash, the active navigation link and the scroll position of a
 * modal's sectioned content in sync.
 *
 * @param visible whether the modal is open
 * @param slugs section ids in the order they appear in the content
 */
export function useSectionScrollSync(visible: boolean, slugs: readonly string[]) {
  const navigate = useNavigate();
  const location = useLocation();
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});
  const contentRef = useRef<HTMLDivElement>(null);
  const previousVisibleRef = useRef(false);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticScrollTimeoutRef = useRef<number | null>(null);

  const activeLink = slugs.includes(location.hash.slice(1))
    ? location.hash.slice(1)
    : slugs[0] || '';

  const registerSection = useCallback(
    (slug: string) => (el: HTMLElement | null) => {
      sectionRefs.current[slug] = el;
    },
    []
  );

  const scrollToSection = useCallback((slug: string) => {
    const section = sectionRefs.current[slug];
    if (section) {
      isProgrammaticScrollRef.current = true;

      if (programmaticScrollTimeoutRef.current !== null) {
        window.clearTimeout(programmaticScrollTimeoutRef.current);
        programmaticScrollTimeoutRef.current = null;
      }

      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.warn(`Section with slug ${slug} not found`);
    }
  }, []);

  const goToSection = useCallback(
    (slug: string) => {
      navigate(
        {
          hash: `#${encodeURIComponent(slug)}`,
        },
        { replace: true }
      );
      scrollToSection(slug);
    },
    [navigate, scrollToSection]
  );

  useEffect(() => {
    const wasVisible = previousVisibleRef.current;

    if (visible && !wasVisible) {
      const hash = location.hash.slice(1);
      const initialSlug = slugs.includes(hash) ? hash : slugs[0] || '';

      navigate(
        {
          hash: `#${encodeURIComponent(initialSlug)}`,
        },
        { replace: true }
      );

      if (sectionRefs.current[initialSlug]) {
        scrollToSection(initialSlug);
      }
    }

    if (!visible && wasVisible) {
      navigate({ hash: '' }, { replace: true });
    }

    previousVisibleRef.current = visible;
  }, [visible, location.hash, navigate, scrollToSection, slugs]);

  useEffect(() => {
    if (!visible) return;
    const scrollContainer = contentRef.current;
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) {
        if (programmaticScrollTimeoutRef.current !== null) {
          window.clearTimeout(programmaticScrollTimeoutRef.current);
        }

        programmaticScrollTimeoutRef.current = window.setTimeout(() => {
          isProgrammaticScrollRef.current = false;
          programmaticScrollTimeoutRef.current = null;
        }, 150);

        return;
      }

      const containerRect = scrollContainer.getBoundingClientRect();
      let current = slugs[0];

      for (const slug of slugs) {
        const section = sectionRefs.current[slug];
        if (section) {
          const sectionTop = section.getBoundingClientRect().top - containerRect.top;
          if (sectionTop <= 80) {
            current = slug;
          }
        }
      }

      // the last section may be too short to ever reach the top of the container
      const isScrolledToBottom =
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - 1;
      if (isScrolledToBottom) {
        current = slugs[slugs.length - 1];
      }
      navigate(
        {
          hash: `#${encodeURIComponent(current)}`,
        },
        { replace: true }
      );
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);

      if (programmaticScrollTimeoutRef.current !== null) {
        window.clearTimeout(programmaticScrollTimeoutRef.current);
        programmaticScrollTimeoutRef.current = null;
      }

      isProgrammaticScrollRef.current = false;
    };
  }, [visible, navigate, slugs]);

  return { activeLink, contentRef, registerSection, goToSection };
}
